import { useTranslation } from 'react-i18next'

import {
  HStack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import {
  FaArrowUpFromBracketRegular,
  FaBoltRegular,
  FaChevronDownRegular,
  FaPlusRegular,
} from '@enechain/ecloud-icons'

import { RichActionMenu } from '~/components/common/RichActionMenu'
import { iconSize } from '~/constants/iconSize'

type Props = {
  /**
   * 札を作れるか (= NormalBidTab の `canCreateBid`。 締切前・読み取り専用でない・
   * 承認フロー設定が解決済み・追加先の下書きが隠れていない)。 作れない間は作成の
   * 導線を出さない (押せない導線で誘導しない)。
   */
  canCreate: boolean
  onCreateSingle: () => void
  onCreateBulk: () => void
  onCreateCsv: () => void
}

/**
 * 受渡日に札が 1 件も無い時の入札一覧の空状態 (spec `spot-bidding.md` AC-BSP-020)。
 *
 * 「入札がありません」 と、 作成の導線を出す。 導線は一覧の見出し右の「入札追加」 と同じ
 * split button (主ボタン = 個別作成、 caret = 一括作成・CSV アップロード)。 design
 * `bidding/spot.html` の `#spot-bid-empty-row` (説明文 + split button) に合わせる。
 * 絞り込みで 0 件になった時は出さない (札はあるので「該当する札がありません」 のまま)。
 *
 * [8080 差異] 見出しは spec の「入札がありません」 (8080 は「まだ入札がありません」)。
 * spec の「札作成」 ボタンは、 dev / 8080 とも「入札追加」 の split button になっている
 * ので、 画面にあるボタン名で案内する。
 */
export const SpotBidEmptyState = ({
  canCreate,
  onCreateSingle,
  onCreateBulk,
  onCreateCsv,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <VStack gap={2} paddingY={10}>
      <Text textStyle="sectionTitle" color="Text.highEmphasis">
        {t('page:biddingSpot.bidTable.noBids')}
      </Text>
      {canCreate && (
        <>
          <Text textStyle="body" color="Text.midEmphasis">
            {t('page:biddingSpot.bidTable.noBidsGuide')}
          </Text>
          {/* 見出し右の split button と同じ組み方 (区切り線は caret の左 border を
              主ボタンの右 border に重ねて 1 本にする。 design/ui-feasibility/components/
              split-button.md「dev 実装の現状」)。 */}
          <HStack gap={0} marginTop={2}>
            <TertiaryButton
              borderRightRadius={0}
              onClick={onCreateSingle}
              size="md"
            >
              <FaPlusRegular boxSize={iconSize.labeledButton} />
              {t('page:biddingSpot.wizard.stepCreate')}
            </TertiaryButton>
            <RichActionMenu
              ariaLabel={t('page:biddingSpot.bidCreateMenu.moreLabel')}
              items={[
                {
                  value: 'bulk',
                  title: t('page:biddingSpot.bidCreateMenu.bulk'),
                  description: t('page:biddingSpot.bidCreateMenu.bulkDesc'),
                  icon: <FaBoltRegular boxSize={iconSize.labeledButton} />,
                  onClick: onCreateBulk,
                },
                {
                  value: 'csv',
                  title: t('page:biddingSpot.bidCreateMenu.csv'),
                  description: t('page:biddingSpot.bidCreateMenu.csvDesc'),
                  icon: (
                    <FaArrowUpFromBracketRegular
                      boxSize={iconSize.labeledButton}
                    />
                  ),
                  onClick: onCreateCsv,
                },
              ]}
              trigger={
                <TertiaryButton
                  marginLeft="-1px"
                  borderLeftWidth="1px"
                  borderLeftColor="color-mix(in srgb, currentColor 25%, transparent)"
                  borderLeftRadius={0}
                  aria-label={t('page:biddingSpot.bidCreateMenu.moreLabel')}
                  paddingX={2}
                  size="md"
                  title={t('page:biddingSpot.bidCreateMenu.moreLabel')}
                >
                  <FaChevronDownRegular boxSize={iconSize.labeledButton} />
                </TertiaryButton>
              }
            />
          </HStack>
        </>
      )}
    </VStack>
  )
}
